import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Navigate } from 'react-router-dom';
import {
  Box,
  Button,
  Divider,
  FormControl,
  GlobalStyles,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import QRCodeStyling from 'qr-code-styling';
import AuthContext from '../../context/authContext';
import useIssue from '../../hooks/useIssue';

const branchLabel = branch => branch?.locationName || branch?.longName || '';
const entertainmentLabel = ent => ent?.longName || ent?.entertainmentName || '';
const entertainmentBranchId = ent => {
  if (!ent?.branch) return '';
  return typeof ent.branch === 'object' ? String(ent.branch._id || '') : String(ent.branch);
};

// Collect all resource ids of an entertainment from both the plain resourceIds
// array and the orderedResources sub-document, deduplicated.
const collectResourceIds = ent => {
  const ids = [];
  (ent?.resourceIds || []).forEach(entry => {
    const id = typeof entry === 'object' ? entry?._id : entry;
    if (id) ids.push(String(id));
  });
  (ent?.orderedResources?.resources || []).forEach(entry => {
    const id = typeof entry?.resourceId === 'object' ? entry.resourceId?._id : entry?.resourceId;
    if (id) ids.push(String(id));
  });
  return [...new Set(ids)];
};

// Deep link into the reporting form; the resourceId is omitted for
// entertainment-level codes ("Gesamtes Entertainment").
const buildReportUrl = ({ branchId, entertainmentId, resourceId }) => {
  const params = new URLSearchParams({ branchId, entertainmentId });
  if (resourceId) params.set('resourceId', resourceId);
  return `${window.location.origin}/issues/report?${params.toString()}`;
};

// Turn a card label into a safe, readable file name part.
const fileNamePart = value =>
  String(value || '')
    .normalize('NFKD')
    .replace(/[^\p{L}\p{N}]+/gu, '-')
    .replace(/^-+|-+$/g, '')
    .toLowerCase() || 'qr-code';

/**
 * Renders one styled QR code into a div. The library appends a canvas, so the
 * cleanup empties the container to avoid duplicates on re-render/strict mode.
 */
function QrCode({ data }) {
  const containerRef = useRef(null);

  useEffect(() => {
    const node = containerRef.current;
    if (!node) return undefined;
    const qrCode = new QRCodeStyling({
      width: 200,
      height: 200,
      data,
      dotsOptions: { type: 'rounded' },
    });
    qrCode.append(node);
    return () => {
      while (node.firstChild) node.removeChild(node.firstChild);
    };
  }, [data]);

  return <div ref={containerRef} style={{ display: 'flex' }} />;
}

/**
 * Printable QR code management: one card per room/resource (plus one per
 * entertainment) linking to the preselected issue reporting form. Cards are
 * grouped per entertainment, printable as a whole and downloadable one by one
 * as high-resolution PNGs.
 */
export default function IssueQrCodesView() {
  const authCtx = useContext(AuthContext);
  const { getStatusOverview } = useIssue();

  const canView = authCtx.checkRight('issue:configure') || authCtx.checkRight('issue:manage');

  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState(null);
  const [branchId, setBranchId] = useState('');
  const [entertainmentFilter, setEntertainmentFilter] = useState('all');

  useEffect(() => {
    if (!canView) return;
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const result = await getStatusOverview();
        if (cancelled || !result) return;
        setOverview(result);
        // Preselect the first branch so the grid is not empty on arrival.
        const firstBranch = result.branches?.[0];
        if (firstBranch) setBranchId(prev => prev || String(firstBranch._id));
      } catch (err) {
        // The hook already showed a German error toast.
        console.error('status overview load failed', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canView]);

  const branches = useMemo(() => overview?.branches || [], [overview]);

  const branchEntertainments = useMemo(
    () =>
      (overview?.entertainments || []).filter(
        ent => ent?.active !== false && entertainmentBranchId(ent) === branchId
      ),
    [overview, branchId]
  );

  const resourcesById = useMemo(
    () => new Map((overview?.resources || []).map(resource => [String(resource._id), resource])),
    [overview]
  );

  const selectedBranch = useMemo(
    () => branches.find(branch => String(branch._id) === branchId) || null,
    [branches, branchId]
  );

  // One section per entertainment, each with one card for the whole
  // entertainment plus one card per room/resource.
  const groups = useMemo(() => {
    const visibleEntertainments =
      entertainmentFilter === 'all'
        ? branchEntertainments
        : branchEntertainments.filter(ent => String(ent._id) === entertainmentFilter);
    return visibleEntertainments.map(ent => {
      const entId = String(ent._id);
      const entName = entertainmentLabel(ent);
      const cards = [
        {
          key: `ent-${entId}`,
          title: 'Gesamtes Entertainment',
          entertainmentName: entName,
          url: buildReportUrl({ branchId, entertainmentId: entId }),
        },
      ];
      collectResourceIds(ent).forEach(resId => {
        const resource = resourcesById.get(resId);
        if (!resource || resource.active === false) return;
        cards.push({
          key: `res-${entId}-${resId}`,
          title: resource.name,
          entertainmentName: entName,
          url: buildReportUrl({ branchId, entertainmentId: entId, resourceId: resId }),
        });
      });
      return { key: entId, name: entName, cards };
    });
  }, [branchEntertainments, entertainmentFilter, branchId, resourcesById]);

  const cardCount = useMemo(
    () => groups.reduce((sum, group) => sum + group.cards.length, 0),
    [groups]
  );

  const handleBranchChange = useCallback(event => {
    setBranchId(event.target.value);
    setEntertainmentFilter('all');
  }, []);

  // Download a single code as a high-resolution PNG, named after the room.
  const handleDownload = useCallback(card => {
    const qrCode = new QRCodeStyling({
      width: 1024,
      height: 1024,
      margin: 32,
      data: card.url,
      dotsOptions: { type: 'rounded' },
      backgroundOptions: { color: '#ffffff' },
    });
    qrCode.download({
      name: `issue-qr-${fileNamePart(card.entertainmentName)}-${fileNamePart(card.title)}`,
      extension: 'png',
    });
  }, []);

  if (!canView) return <Navigate to='/' replace />;

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1200, mx: 'auto' }}>
      <GlobalStyles
        styles={{
          '@media print': {
            'body *': { visibility: 'hidden' },
            '.issue-qr-print-area, .issue-qr-print-area *': { visibility: 'visible' },
            '.issue-qr-print-area': {
              position: 'absolute',
              left: 0,
              top: 0,
              width: '100%',
              margin: 0,
              padding: 0,
              backgroundColor: '#fff',
            },
            '.issue-qr-card': {
              breakInside: 'avoid',
              pageBreakInside: 'avoid',
              backgroundColor: '#fff',
              color: '#000',
              boxShadow: 'none',
            },
          },
        }}
      />

      <Stack
        className='issue-qr-controls'
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        alignItems={{ sm: 'center' }}
        sx={{ mb: 3, displayPrint: 'none' }}
      >
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant='h5' sx={{ fontWeight: 700 }}>
            QR-Codes für Issue-Meldungen
          </Typography>
          <Typography variant='body2' color='text.secondary'>
            Codes ausdrucken oder einzeln herunterladen und in den Räumen aufhängen – ein Scan
            öffnet das vorausgefüllte Meldeformular.
          </Typography>
        </Box>
        <FormControl size='small' sx={{ minWidth: 200 }}>
          <InputLabel id='issue-qr-branch-label'>Standort</InputLabel>
          <Select
            labelId='issue-qr-branch-label'
            label='Standort'
            value={branchId}
            onChange={handleBranchChange}
            disabled={loading}
          >
            {branches.map(branch => (
              <MenuItem key={branch._id} value={String(branch._id)}>
                {branchLabel(branch)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size='small' sx={{ minWidth: 200 }}>
          <InputLabel id='issue-qr-entertainment-label'>Entertainment</InputLabel>
          <Select
            labelId='issue-qr-entertainment-label'
            label='Entertainment'
            value={entertainmentFilter}
            onChange={event => setEntertainmentFilter(event.target.value)}
            disabled={loading || !branchId}
          >
            <MenuItem value='all'>Alle</MenuItem>
            {branchEntertainments.map(ent => (
              <MenuItem key={ent._id} value={String(ent._id)}>
                {entertainmentLabel(ent)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button
          variant='contained'
          startIcon={<PrintIcon />}
          onClick={() => window.print()}
          disabled={cardCount === 0}
        >
          Alle drucken
        </Button>
      </Stack>

      {loading ? (
        <Grid container spacing={2}>
          {[0, 1, 2].map(index => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Skeleton variant='rounded' height={320} />
            </Grid>
          ))}
        </Grid>
      ) : cardCount === 0 ? (
        <Typography color='text.secondary'>
          Keine Räume gefunden – bitte Standort und Entertainment wählen.
        </Typography>
      ) : (
        <Stack spacing={4} className='issue-qr-print-area'>
          {groups.map(group => (
            <Box key={group.key}>
              <Stack
                direction='row'
                alignItems='center'
                spacing={2}
                sx={{ mb: 2, displayPrint: 'none' }}
              >
                <Typography variant='h6' sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>
                  {group.name}
                </Typography>
                <Divider sx={{ flexGrow: 1 }} />
                <Typography variant='caption' color='text.secondary' sx={{ whiteSpace: 'nowrap' }}>
                  {group.cards.length} {group.cards.length === 1 ? 'Code' : 'Codes'}
                </Typography>
              </Stack>
              <Grid container spacing={2}>
                {group.cards.map(card => (
                  <Grid item xs={12} sm={6} md={4} key={card.key}>
                    <Paper
                      className='issue-qr-card'
                      variant='outlined'
                      sx={{
                        p: 2.5,
                        position: 'relative',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        textAlign: 'center',
                        gap: 1.25,
                        height: '100%',
                        borderRadius: 3,
                      }}
                    >
                      <Tooltip title='Als PNG herunterladen'>
                        <IconButton
                          size='small'
                          onClick={() => handleDownload(card)}
                          sx={{
                            position: 'absolute',
                            top: 8,
                            right: 8,
                            displayPrint: 'none',
                          }}
                        >
                          <FileDownloadOutlinedIcon fontSize='small' />
                        </IconButton>
                      </Tooltip>
                      <Box
                        sx={{
                          p: 1.5,
                          mt: 1,
                          backgroundColor: '#fff',
                          borderRadius: 2,
                          border: '1px solid',
                          borderColor: 'divider',
                          display: 'flex',
                        }}
                      >
                        <QrCode data={card.url} />
                      </Box>
                      <Box>
                        <Typography variant='h6' sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                          {card.title}
                        </Typography>
                        <Typography variant='caption' color='text.secondary'>
                          {card.entertainmentName}
                          {selectedBranch ? ` – ${branchLabel(selectedBranch)}` : ''}
                        </Typography>
                      </Box>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Box>
          ))}
        </Stack>
      )}
    </Box>
  );
}
